import React, { useState, useEffect, useCallback } from 'react';
import { User, Phone, Lock, Mail, MessageCircle, Save, Eye, EyeOff, CheckCircle2, Loader2, AlertCircle, Building2, CreditCard, Unlink, ShieldCheck, Monitor, Smartphone, Tablet, X, Sparkles, Package, ArrowRight, Calendar, RotateCcw, Info, Receipt } from 'lucide-react';
import * as UAParserLib from 'ua-parser-js';
import { useTelegramLink } from '../hooks/useTelegramLink';
import { TelegramLinkPanel } from '../components/TelegramLinkPanel';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api, { clearTokens } from '../api';
import CompanyPdfUpload from '../components/CompanyPdfUpload';
import { companyPdfErrorKey, uploadCompanyPdf } from '../api/companyPdf';
import { formatFinancialInput, formatFinancialValue, isFinancialInputComplete } from '../utils/financialInput';
import { PhoneInput } from '../components/PhoneInput';
import Pagination from '../components/Pagination';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { checkoutServices, getSavedCards, deleteSavedCard, type PaymentMethod, type SavedCard } from '../api/billing';
import { useEpayPayment } from '../hooks/useEpayPayment';
import PaymentMethodToggle from '../components/PaymentMethodToggle';
import EpayQrModal from '../components/EpayQrModal';
import { ProcurementPlanSummary } from '../components/ProcurementPlanEditor';
import ProcurementTrialPanel from '../components/ProcurementTrialPanel';
import ServiceCreditsSummary from '../components/ServiceCreditsSummary';

const getErrorMessage = (error: any, defaultMsg: string | undefined): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0 && detail[0].msg) return detail[0].msg;
  return defaultMsg || 'Произошла неизвестная ошибка';
};

// Safely parse a datetime string from the backend.
// FastAPI often returns naive ISO strings without a timezone suffix (e.g. "2024-01-15T10:30:00").
// Without "Z"/"+00:00" Safari treats them as Invalid Date, so we normalise here.
const parseDateStr = (raw: string | null | undefined): Date | null => {
  if (!raw) return null;
  // If no timezone indicator, treat as UTC by appending Z
  const normalised = /[Z+-]\d*$/.test(raw.trim()) ? raw : raw + 'Z';
  const d = new Date(normalised);
  return isNaN(d.getTime()) ? null : d;
};

const fmtDateTime = (raw: string | null | undefined): string => {
  const d = parseDateStr(raw);
  return d ? d.toLocaleString('ru-RU') : '—';
};

const fmtDate = (raw: string | null | undefined): string => {
  const d = parseDateStr(raw);
  return d ? d.toLocaleDateString('ru-RU') : '—';
};

interface SessionItem {
  session_id: string;
  ip: string;
  user_agent: string;
  created_at: string;
  expires_at: string;
}

interface OrderItemRow {
  item_type: string;
  quantity: number;
  unit_price: number;
}
interface OrderRow {
  id: string;
  amount: number;
  currency: string;
  items: OrderItemRow[];
  status: string;
  created_at: string;
}

const ORDERS_PAGE_SIZE = 10;
const fmtMoney = (n: number): string => new Intl.NumberFormat('ru-RU').format(n);

export default function ProfilePage() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const { user, subscription, refreshUser } = useAuth();
  const navigate = useNavigate();
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    authProvider: '',
    email: '',
  });

  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });

  const [notificationData, setNotificationData] = useState({
    whatsapp: '',
    telegram: '',
  });
  
  const [isNotifEditing, setIsNotifEditing] = useState(false);
  const [isNotifLoading, setIsNotifLoading] = useState(false);
  const [initialNotifData, setInitialNotifData] = useState({ whatsapp: '', telegram: '' });
  const [notifSaveStatus, setNotifSaveStatus] = useState({ type: '', message: '' });
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [unlinkMsg, setUnlinkMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [telegramToast, setTelegramToast] = useState('');

  // Company profile state
  const fmtInput = formatFinancialInput;
  const parseNum = (s: string) => parseFloat(s.replace(/[\u00a0\s]/g, '')) || 0;
  const [companyData, setCompanyData] = useState({
    income_3yrs: '', taxes_3yrs: '', pfot_3yrs: '',
    history_income_year_1: '', history_income_year_2: '',
    history_taxes_year_1: '', history_taxes_year_2: '',
    history_pfot_year_1: '', history_pfot_year_2: '',
  });
  const [isCompanyEditing, setIsCompanyEditing] = useState(false);
  const [isCompanyLoading, setIsCompanyLoading] = useState(false);
  const [companySaveStatus, setCompanySaveStatus] = useState({ type: '', message: '' });
  const [isPdfParsing, setIsPdfParsing] = useState(false);
  const [pdfUploadStatus, setPdfUploadStatus] = useState({ type: '', message: '' });

  // Billing state
  const [prices, setPrices] = useState<{ pfu_calculation: number; vip_calculation: number; tracker_slot: number; planning_calculation?: number; tier_requirements_calculation?: number; experience?: number | null; discounts?: Record<string, number> } | null>(null);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [quantities, setQuantities] = useState({ pfu: 0, vip: 0, slot: 0, planning: 0, tier: 0, experience: 0 });
  const [isBuying, setIsBuying] = useState(false);
  const [billingStatus, setBillingStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [buyMethod, setBuyMethod] = useState<PaymentMethod>('card');
  const { qrState, qrPolling, handlePaymentInit, closeQr } = useEpayPayment(
    async () => { await refreshUser(); setBillingStatus({ type: 'success', msg: t('billing.buySuccess') }); },
    () => setBillingStatus({ type: 'error', msg: t('plans.qrFailed') }),
  );

  // Subscription state
  const [isCancelling, setIsCancelling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [subActionMsg, setSubActionMsg] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Saved cards (ePay tokens for subscription auto-renewal)
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [confirmDeleteCard, setConfirmDeleteCard] = useState<SavedCard | null>(null);
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);

  const fetchSavedCards = useCallback(async () => {
    try {
      setSavedCards(await getSavedCards());
    } catch {
      // non-critical — section just stays empty
    }
  }, []);

  useEffect(() => { fetchSavedCards(); }, [fetchSavedCards]);

  const handleDeleteCard = async () => {
    if (!confirmDeleteCard) return;
    setDeletingCardId(confirmDeleteCard.id);
    try {
      await deleteSavedCard(confirmDeleteCard.id);
      setSavedCards((cards) => cards.filter((c) => c.id !== confirmDeleteCard.id));
      setSubActionMsg({ type: 'success', msg: t('subscription.cardDeleted') });
      await refreshUser();
    } catch {
      setSubActionMsg({ type: 'error', msg: t('subscription.cardDeleteError') });
    } finally {
      setDeletingCardId(null);
      setConfirmDeleteCard(null);
    }
  };

  // Security state
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  const [terminatingId, setTerminatingId] = useState<string | null>(null);
  const [confirmTerminate, setConfirmTerminate] = useState<SessionItem | null>(null);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);
  const [logoutAllConfirm, setLogoutAllConfirm] = useState(false);
  const [securityMsg, setSecurityMsg] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  // ID of the session the user is currently authenticated with (from backend)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Payment history state
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotal, setOrdersTotal] = useState(0);

  const fetchOrders = useCallback(async (page: number) => {
    setOrdersLoading(true); setOrdersError('');
    try {
      const res = await api.get('/billing/orders', {
        params: { skip: (page - 1) * ORDERS_PAGE_SIZE, limit: ORDERS_PAGE_SIZE },
      });
      setOrders(res.data.items ?? []);
      setOrdersTotal(res.data.total ?? 0);
    } catch {
      setOrdersError(t('profile.payments.error'));
    } finally {
      setOrdersLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderItemLabel = (itemType: string): string => {
    const map: Record<string, string> = {
      pfu_calculation: t('billing.pfuItem'),
      vip_calculation: t('billing.vipItem'),
      tracker_slot: t('billing.slotItem'),
      planning_calculation: t('billing.planningItem'),
      tier_requirements_calculation: t('billing.tierItem'),
      experience: t('billing.experienceItem'),
    };
    if (map[itemType]) return map[itemType];
    if (itemType.startsWith('subscription_renewal:')) return t('profile.payments.subRenewal');
    if (itemType.startsWith('subscription:')) return t('profile.payments.subscription');
    return itemType;
  };

  const renderOrderItems = (items: OrderItemRow[]) => (
    <div className="flex flex-wrap gap-1.5">
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1 rounded-lg bg-gray-50 border border-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
          {orderItemLabel(it.item_type)}
          {it.quantity > 1 && <span className="font-bold text-gray-800">×{it.quantity}</span>}
        </span>
      ))}
    </div>
  );

  const renderOrderStatus = (status: string) => {
    const map: Record<string, { label: string; cls: string }> = {
      paid: { label: t('profile.payments.statusPaid'), cls: 'bg-green-50 text-green-700 border-green-100' },
      paid_test: { label: t('profile.payments.statusPaid'), cls: 'bg-green-50 text-green-700 border-green-100' },
      pending: { label: t('profile.payments.statusPending'), cls: 'bg-amber-50 text-amber-700 border-amber-100' },
      failed: { label: t('profile.payments.statusFailed'), cls: 'bg-red-50 text-red-600 border-red-100' },
      refunded: { label: t('profile.payments.statusRefunded'), cls: 'bg-gray-100 text-gray-500 border-gray-200' },
    };
    const s = map[status] ?? { label: status, cls: 'bg-gray-100 text-gray-500 border-gray-200' };
    return (
      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold whitespace-nowrap ${s.cls}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
        {s.label}
      </span>
    );
  };

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true); setSessionsError('');
    try {
      const res = await api.get('/auth/sessions');
      setSessions(res.data.sessions ?? []);
      // Backend may return which session is the active one
      if (res.data.current_session_id) {
        setCurrentSessionId(res.data.current_session_id);
      }
    } catch {
      setSessionsError('Ошибка сети. Попробуйте ещё раз.');
    } finally {
      setSessionsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // stable — no deps that change at runtime

  const handleTerminateSession = async (sess: SessionItem) => {
    setTerminatingId(sess.session_id);
    try {
      await api.delete(`/auth/sessions/${sess.session_id}`);
      // If the user just deleted their OWN current session, the cookie is now
      // invalid. Don't attempt another authenticated request — clear tokens and
      // redirect immediately instead of risking a cascade of 401s.
      if (currentSessionId && sess.session_id === currentSessionId) {
        clearTokens();
        navigate('/login');
        return;
      }
      await fetchSessions();
      setSecurityMsg({ type: 'success', msg: t('profile.security.sessionTerminateSuccess') });
    } catch {
      setSecurityMsg({ type: 'error', msg: t('profile.security.errNetwork') });
    } finally {
      setTerminatingId(null);
      setConfirmTerminate(null);
    }
  };

  const handleLogoutAll = async () => {
    setLogoutAllLoading(true);
    try {
      await api.post('/auth/logout-all');
      // Backend terminates ALL sessions including the current one.
      // Clear local tokens and redirect — do NOT call fetchSessions (would 401).
      clearTokens();
      navigate('/login');
    } catch {
      setSecurityMsg({ type: 'error', msg: t('profile.security.errNetwork') });
      setLogoutAllLoading(false);
      setLogoutAllConfirm(false);
    }
  };

  const getDeviceIcon = (ua: string) => {
    const parser = new UAParserLib.UAParser(ua);
    const deviceType = parser.getDevice().type;
    if (deviceType === 'mobile') return <Smartphone className="w-5 h-5 text-goszakup-500" />;
    if (deviceType === 'tablet') return <Tablet className="w-5 h-5 text-goszakup-500" />;
    return <Monitor className="w-5 h-5 text-goszakup-500" />;
  };

  const getDeviceLabel = (ua: string) => {
    const parser = new UAParserLib.UAParser(ua);
    const browser = parser.getBrowser().name || '';
    const os = parser.getOS().name || '';
    return [browser, os].filter(Boolean).join(' / ') || t('profile.security.unknownDevice');
  };

  // Sidebar navigation
  const [activeSection, setActiveSection] = useState('profile');
  useEffect(() => {
    const section = location.hash.slice(1);
    if (!['profile', 'notifications', 'password', 'company', 'billing', 'subscription', 'security', 'payments'].includes(section)) return;
    const frame = requestAnimationFrame(() => document.getElementById(section)?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, [location.hash]);
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    const scrollEl = document.getElementById('main-scroll');
    const sections = ['profile', 'notifications', 'password', 'company', 'billing', 'security', 'payments'];
    const observers: IntersectionObserver[] = [];
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { root: scrollEl, rootMargin: '-20% 0px -70% 0px', threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  useEffect(() => {
    setPricesLoading(true);
    api.get('/billing/prices')
      .then(res => setPrices(res.data))
      .catch(() => {})
      .finally(() => setPricesLoading(false));
  }, [subscription?.plan_id, subscription?.status]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    fetchOrders(ordersPage);
  }, [ordersPage, fetchOrders]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.name || '',
        lastName: user.surname || '',
        phone: user.phone_number || '',
        authProvider: user.auth_provider || 'local',
        email: user.email || '',
      });
      
      const w = user.whatsapp_number || '';
      const t = user.telegram_id || '';
      setNotificationData({ whatsapp: w, telegram: t });
      setInitialNotifData({ whatsapp: w, telegram: t });
      
      if (!w) {
        setIsNotifEditing(true);
      } else {
        setIsNotifEditing(false);
      }

      if (user.company_profile) {
        const cp = user.company_profile;
        setCompanyData({
          income_3yrs: formatFinancialValue(cp.income_3yrs),
          taxes_3yrs: formatFinancialValue(cp.taxes_3yrs),
          pfot_3yrs: formatFinancialValue(cp.pfot_3yrs),
          history_income_year_1: cp.history_income_year_1 != null ? formatFinancialValue(cp.history_income_year_1) : '',
          history_income_year_2: cp.history_income_year_2 != null ? formatFinancialValue(cp.history_income_year_2) : '',
          history_taxes_year_1: cp.history_taxes_year_1 != null ? formatFinancialValue(cp.history_taxes_year_1) : '',
          history_taxes_year_2: cp.history_taxes_year_2 != null ? formatFinancialValue(cp.history_taxes_year_2) : '',
          history_pfot_year_1: cp.history_pfot_year_1 != null ? formatFinancialValue(cp.history_pfot_year_1) : '',
          history_pfot_year_2: cp.history_pfot_year_2 != null ? formatFinancialValue(cp.history_pfot_year_2) : '',
        });
        setIsCompanyEditing(false);
      } else {
        setIsCompanyEditing(true);
      }
    }
  }, [user]);

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    emailCode: '',
  });

  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '' });
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveStatus({ type: '', message: '' });
    try {
      await api.put('/users/me', {
        name: profileData.firstName,
        surname: profileData.lastName,
        phone_number: profileData.phone
      });
      await refreshUser();
      setSaveStatus({ type: 'success', message: t('profile.profileSaved') });
    } catch (error: any) {
      setSaveStatus({ type: 'error', message: getErrorMessage(error, t('profile.profileSaveError')) });
    }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordData.newPassword) {
      setPasswordStatus({ type: 'error', message: t('profile.fillPasswordsRequired') || 'Пожалуйста, заполните пароль' });
      return;
    }
    if (passwordData.newPassword.length < 8 || !/[A-Z]/.test(passwordData.newPassword) || !/\d/.test(passwordData.newPassword)) {
      setPasswordStatus({ type: 'error', message: t('auth.passwordRequirements') || 'Пароль должен содержать минимум 8 символов, включая 1 заглавную букву и 1 цифру.' });
      return;
    }
    if (!isCodeSent) {
      setIsCodeModalOpen(true);
      return;
    }

    if (!passwordData.emailCode) {
      setPasswordStatus({ type: 'error', message: t('profile.codeLabel') + '...' });
      return;
    }

    setIsSubmittingPassword(true);
    setPasswordStatus({ type: '', message: '' });
    
    try {
      await api.post('/auth/reset-password', {
        email: profileData.email,
        code: passwordData.emailCode,
        new_password: passwordData.newPassword
      });
      setPasswordStatus({ type: 'success', message: t('profile.passwordChangedMsg') });
      setPasswordData({ newPassword: '', emailCode: '' });
      setIsCodeSent(false);
      setTimeout(() => {
        navigate('/login', { state: { message: t('profile.passwordChangedMsg') } });
      }, 2000);
    } catch (error: any) {
      if (error.response?.status === 429) {
        setPasswordStatus({ type: 'error', message: t('errors.too_many_requests') });
      } else if (error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          setPasswordStatus({ type: 'error', message: detail[0].msg });
        } else {
          setPasswordStatus({ type: 'error', message: t('auth.passwordRequirements') });
        }
      } else if (error.response?.status === 400) {
        setPasswordStatus({ type: 'error', message: t('errors.invalid_code') });
      } else {
        setPasswordStatus({ type: 'error', message: t('errors.default') });
      }
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleSendCode = async () => {
    if (resendCooldown > 0) return;
    setIsCodeModalOpen(false);
    setIsSendingCode(true);
    setPasswordStatus({ type: '', message: '' });
    
    try {
      await api.post('/auth/forgot-password', { email: profileData.email });
      setIsCodeSent(true);
      setPasswordStatus({ type: 'success', message: t('profile.codeSentMsg') });
      setResendCooldown(15);
    } catch (error: any) {
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
          setPasswordStatus({ type: 'error', message: t('errors.too_many_requests') });
        // Try to parse wait time or just default to UI block
        let waitSecs = 15;
        if (typeof detail === 'string') {
           const match = detail.match(/(\d+)\s*(min|sec)/i);
           if (match) {
             waitSecs = match[2].toLowerCase().startsWith('min') ? parseInt(match[1]) * 60 : parseInt(match[1]);
           }
        }
        setResendCooldown(waitSecs);
      } else {
        setPasswordStatus({ type: 'error', message: t('errors.default') });
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const saveNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotifSaveStatus({ type: '', message: '' });
    setIsNotifLoading(true);
    try {
      await api.put('/users/me', {
        name: profileData.firstName,
        surname: profileData.lastName,
        phone_number: profileData.phone,
        whatsapp_number: notificationData.whatsapp,
      });
      await refreshUser();
      setNotifSaveStatus({ type: 'success', message: t('profile.profileSaved') });
    } catch (error: any) {
      setNotifSaveStatus({ type: 'error', message: getErrorMessage(error, t('profile.profileSaveError')) });
    } finally {
      setIsNotifLoading(false);
    }
  };

  const handleEditNotifToggle = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsNotifEditing(true);
    setNotifSaveStatus({ type: '', message: '' });
  };

  const handleCancelNotifEdit = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setNotificationData(initialNotifData);
    setIsNotifEditing(false);
    setNotifSaveStatus({ type: '', message: '' });
  };

  // Telegram link/unlink logic — hook manages polling automatically
  const { step: telegramStep, linkData, startLink, reset: resetTelegram } = useTelegramLink(() => {
    // Called when telegram_id appears after polling — show a toast
    setTelegramToast(t('profile.telegramSuccessToast'));
  });

  // Auto-dismiss success toast
  useEffect(() => {
    if (!telegramToast) return;
    const timer = setTimeout(() => setTelegramToast(''), 4000);
    return () => clearTimeout(timer);
  }, [telegramToast]);

  const handleTelegramUnlink = async () => {
    setIsUnlinking(true);
    setUnlinkMsg(null);
    try {
      await api.delete('/auth/telegram/unlink');
      await refreshUser();
      resetTelegram();
      setUnlinkMsg({ type: 'success', text: t('profile.telegramUnlinkSuccess') });
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 400) {
        // Already unlinked on server — just sync state
        await refreshUser();
        resetTelegram();
      } else {
        setUnlinkMsg({ type: 'error', text: t('profile.telegramUnlinkError') });
      }
    } finally {
      setIsUnlinking(false);
    }
  };

  const saveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPdfParsing || isCompanyLoading) return;
    setIsCompanyLoading(true);
    setCompanySaveStatus({ type: '', message: '' });
    try {
      await api.put('/users/me', {
        name: profileData.firstName,
        surname: profileData.lastName,
        company_profile: {
          income_3yrs: parseNum(companyData.income_3yrs),
          taxes_3yrs: parseNum(companyData.taxes_3yrs),
          pfot_3yrs: parseNum(companyData.pfot_3yrs),
          history_income_year_1: isFinancialInputComplete(companyData.history_income_year_1) ? parseNum(companyData.history_income_year_1) : null,
          history_income_year_2: isFinancialInputComplete(companyData.history_income_year_2) ? parseNum(companyData.history_income_year_2) : null,
          history_taxes_year_1: isFinancialInputComplete(companyData.history_taxes_year_1) ? parseNum(companyData.history_taxes_year_1) : null,
          history_taxes_year_2: isFinancialInputComplete(companyData.history_taxes_year_2) ? parseNum(companyData.history_taxes_year_2) : null,
          history_pfot_year_1: isFinancialInputComplete(companyData.history_pfot_year_1) ? parseNum(companyData.history_pfot_year_1) : null,
          history_pfot_year_2: isFinancialInputComplete(companyData.history_pfot_year_2) ? parseNum(companyData.history_pfot_year_2) : null,
        },
      });
      await refreshUser();
      setCompanySaveStatus({ type: 'success', message: t('profile.companySaved') });
      setIsCompanyEditing(false);
    } catch (error: any) {
      setCompanySaveStatus({ type: 'error', message: getErrorMessage(error, t('profile.companySaveError')) });
    } finally {
      setIsCompanyLoading(false);
    }
  };

  const handleCompanyPdfUpload = async (file: File) => {
    if (isPdfParsing || isCompanyLoading) return;
    if (!file.name.toLowerCase().endsWith('.pdf') || file.size > 5 * 1024 * 1024) {
      setPdfUploadStatus({ type: 'error', message: t('profile.companyPdfError400') });
      return;
    }
    setIsPdfParsing(true);
    setPdfUploadStatus({ type: '', message: '' });
    try {
      const data = await uploadCompanyPdf(file);
      const { income_3yrs, taxes_3yrs, pfot_3yrs,
        history_income_year_1, history_income_year_2,
        history_taxes_year_1, history_taxes_year_2,
        history_pfot_year_1, history_pfot_year_2 } = data;
      const fmtV = formatFinancialValue;
      setCompanyData({
        income_3yrs: formatFinancialValue(income_3yrs),
        taxes_3yrs: formatFinancialValue(taxes_3yrs),
        pfot_3yrs: formatFinancialValue(pfot_3yrs),
        history_income_year_1: fmtV(history_income_year_1),
        history_income_year_2: fmtV(history_income_year_2),
        history_taxes_year_1: fmtV(history_taxes_year_1),
        history_taxes_year_2: fmtV(history_taxes_year_2),
        history_pfot_year_1: fmtV(history_pfot_year_1),
        history_pfot_year_2: fmtV(history_pfot_year_2),
      });
      await refreshUser();
      setPdfUploadStatus({ type: 'success', message: t('profile.companyPdfSuccess') });
      setIsCompanyEditing(false);
    } catch (error: any) {
      setPdfUploadStatus({
        type: 'error',
        message: t(companyPdfErrorKey(error, 'profile')),
      });
    } finally {
      setIsPdfParsing(false);
    }
  };

  const handleBuyLimits = async () => {
    const items: { item_type: string; quantity: number }[] = [];
    if (quantities.pfu > 0) items.push({ item_type: 'pfu_calculation', quantity: quantities.pfu });
    if (quantities.vip > 0) items.push({ item_type: 'vip_calculation', quantity: quantities.vip });
    if (quantities.slot > 0) items.push({ item_type: 'tracker_slot', quantity: quantities.slot });
    if (quantities.planning > 0) items.push({ item_type: 'planning_calculation', quantity: quantities.planning });
    if (quantities.tier > 0) items.push({ item_type: 'tier_requirements_calculation', quantity: quantities.tier });
    if (quantities.experience > 0 && prices?.experience != null) items.push({ item_type: 'experience', quantity: quantities.experience });
    if (items.length === 0) return;
    setIsBuying(true); setBillingStatus(null);
    try {
      const init = await checkoutServices(items, buyMethod);
      setQuantities({ pfu: 0, vip: 0, slot: 0, planning: 0, tier: 0, experience: 0 });
      await handlePaymentInit(init);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      let msg = t('billing.buyError');
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic v2 returns array of {msg, loc, type}; body-level errors have loc=['body']
        msg = detail.map((d: any) => d.msg ?? '').filter(Boolean).join('. ') || msg;
      }
      setBillingStatus({ type: 'error', msg });
    } finally { setIsBuying(false); }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    setSubActionMsg(null);
    try {
      await api.post('/subscriptions/cancel');
      await refreshUser();
      setShowCancelConfirm(false);
      setSubActionMsg({ type: 'success', msg: t('subscription.cancelSuccess') });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setSubActionMsg({ type: 'error', msg: typeof detail === 'string' ? detail : t('subscription.actionError') });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!subscription?.plan_id) return;
    setIsResuming(true);
    setSubActionMsg(null);
    try {
      await api.post('/subscriptions/resume');
      await refreshUser();
      setSubActionMsg({ type: 'success', msg: t('subscription.resumeSuccess') });
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setSubActionMsg({ type: 'error', msg: typeof detail === 'string' ? detail : t('subscription.actionError') });
    } finally {
      setIsResuming(false);
    }
  };

  return (
    <div className="workspace-page !max-w-6xl">
      <div className="mb-8 grid gap-6">
        <header>
          <h1 className="workspace-title">{language === 'kk' ? 'Жеке кабинет' : 'Личный кабинет'}</h1>
          <p className="workspace-subtitle">{language === 'kk' ? 'Компания деректері, хабарламалар және қызметтерге қолжетімділік.' : 'Данные компании, уведомления и доступ к услугам.'}</p>
        </header>
        <ProcurementTrialPanel compact />
      </div>
      <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-8">
        {/* Main content */}
        <div className="min-w-0 space-y-8">
          
          {/* Profile Details */}
          <div id="profile" className="scroll-mt-6 workspace-card">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <User className="w-6 h-6 text-goszakup-600" /> 
              Основные данные
            </h2>
            <form onSubmit={saveProfile} className="space-y-5">
              {saveStatus.message && (
                <div className={`p-3 rounded-xl text-sm ${saveStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                  {saveStatus.message}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Имя</label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 text-sm transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Фамилия</label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    className="block w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 text-sm transition-all"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Номер телефона</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400 z-10" />
                  </div>
                  <PhoneInput
                    name="phone"
                    required
                    value={profileData.phone}
                    onChange={(val) => setProfileData(prev => ({ ...prev, phone: val }))}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button type="submit" className="inline-flex items-center gap-2 px-6 py-3 bg-goszakup-600 text-white rounded-xl font-bold hover:bg-goszakup-700 transition-colors shadow-sm">
                  <Save className="w-4 h-4" />
                  Сохранить данные
                </button>
              </div>
            </form>
          </div>

          {/* Password Change */}
          <div id="password" className="scroll-mt-6 workspace-card relative">
            <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Lock className="w-6 h-6 text-goszakup-600" />
              {t('profile.passwordTitle')}
            </h2>
            
            {profileData.authProvider === 'google' ? (
              <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm border border-blue-100">
                {t('profile.googleAuthNotice')}
              </div>
            ) : (
              <form onSubmit={savePassword} className="space-y-5">
                {passwordStatus.message && (
                  <div className={`p-4 rounded-xl text-sm font-medium ${
                    passwordStatus.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-700 border border-green-100'
                  }`}>
                    {passwordStatus.message}
                  </div>
                )}
                
                {!isCodeSent ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-1 gap-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.newPasswordLabel')}</label>
                        <div className="relative">
                          <input
                            type={showNewPassword ? "text" : "password"}
                            name="newPassword"
                            required
                            value={passwordData.newPassword}
                            onChange={handlePasswordChange}
                            minLength={8}
                            className="block w-full px-4 pr-10 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 text-sm transition-all"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="text-gray-400 hover:text-gray-500 focus:outline-none"
                              tabIndex={-1}
                            >
                              {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                            </button>
                          </div>
                        </div>
                        {passwordData.newPassword && (passwordData.newPassword.length < 8 || !/[A-Z]/.test(passwordData.newPassword) || !/\d/.test(passwordData.newPassword)) ? (
                          <p className="text-xs text-red-500 mt-1">{t('auth.passwordRequirements')}</p>
                        ) : (
                          <p className="text-xs text-gray-500 mt-1">{t('profile.passwordMinLength') || 'Пароль должен содержать минимум 8 символов, включая 1 заглавную букву и 1 цифру.'}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end pt-2">
                      <button 
                         type="submit" 
                         disabled={isSendingCode || resendCooldown > 0} 
                         className="inline-flex items-center gap-2 px-6 py-3 bg-goszakup-600 text-white rounded-xl font-bold hover:bg-goszakup-700 transition-colors shadow-sm disabled:bg-gray-400"
                      >
                        <Lock className="w-4 h-4" />
                        {isSendingCode ? '...' : (resendCooldown > 0 ? `${t('profile.changePasswordBtn')} (${resendCooldown}с)` : t('profile.changePasswordBtn'))}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-orange-50 text-orange-800 p-4 rounded-xl text-sm border border-orange-100 mb-4">
                      {t('profile.codeWarning')}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-1 gap-5 opacity-60 pointer-events-none mb-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.newPasswordLabel')}</label>
                        <input
                          type="password"
                          value={passwordData.newPassword}
                          readOnly
                          className="block w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t('profile.codeLabel')}</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          name="emailCode"
                          required
                          value={passwordData.emailCode}
                          onChange={handlePasswordChange}
                          className="block w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 text-sm transition-all"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-5">
                      <button type="submit" disabled={isSubmittingPassword} className="inline-flex items-center gap-2 px-6 py-3 bg-goszakup-600 text-white rounded-xl font-bold hover:bg-goszakup-700 transition-colors shadow-sm disabled:bg-gray-400">
                        <Save className="w-4 h-4" />
                        {isSubmittingPassword ? '...' : t('profile.changePasswordBtn')}
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}

            {/* Modal for confirmation */}
            {isCodeModalOpen && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{t('profile.modalTitle')}</h3>
                  <p className="text-gray-600 mb-8">{t('profile.modalDesc')} <b>{profileData.email}</b>?</p>
                  <div className="flex justify-end gap-3">
                    <button 
                      type="button" 
                      onClick={() => setIsCodeModalOpen(false)}
                      className="px-5 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                    >
                      {t('profile.modalCancel')}
                    </button>
                    <button 
                      type="button" 
                      onClick={handleSendCode}
                      className="px-5 py-2.5 text-white bg-goszakup-600 hover:bg-goszakup-700 rounded-xl font-medium transition-colors"
                    >
                      {t('profile.modalConfirm')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notification Settings */}
          <div id="notifications" className="scroll-mt-6 workspace-card">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-goszakup-600" />
              {t('profile.notifTitle')}
            </h2>

            <p className="text-sm text-gray-600 mb-6 leading-relaxed bg-blue-50 p-4 rounded-xl border border-blue-100">
              {t('profile.notifDesc')}
            </p>

            <form onSubmit={saveNotifications} className="space-y-6">

              {notifSaveStatus.message && (
                <div className={`p-3 rounded-xl text-sm ${notifSaveStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                  {notifSaveStatus.message}
                </div>
              )}

              {/* WhatsApp */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.405-.883-.733-1.48-1.638-1.653-1.935-.173-.298-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51h-.57c-.198 0-.52.074-.792.347-.272.273-1.04 1.018-1.04 2.479 0 1.46 1.065 2.871 1.213 3.07.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp Номер
                </label>
                <PhoneInput
                  name="whatsapp"
                  value={notificationData.whatsapp}
                  onChange={(val) => setNotificationData(prev => ({ ...prev, whatsapp: val }))}
                  disabled={!isNotifEditing}
                  className="focus:ring-green-500 focus:border-green-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
              </div>

              {/* Telegram */}
              <div className="pt-2 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.699 1.203-1.201 1.22-.506.017-1.267-.323-2.028-.823-1.077-.709-1.744-1.127-2.671-1.737-.991-.65-1.731-1.002-2.477-1.465-.746-.462-1.401-1.002-1.34-1.34.03-.166.471-.62.91-1.05.44-.43 2.502-2.316 2.553-2.528.05-.21-.013-.306-.118-.306-.104 0-1.895 1.189-3.238 2.083-.19.125-.395.187-.584.218-.19.03-.497.087-.803.05-.306-.038-.891-.186-1.258-.337-.367-.15-.658-.23-.623-.487.035-.257.377-.52.713-.706 2.766-1.528 5.679-2.88 8.761-4.045 1.05-.397 2.112-.767 3.167-1.053.491-.133.916-.179 1.25-.133.334.045.545.185.748.455.056.074.116.27.051.52zm-3.69 11.233z" />
                  </svg>
                  Telegram
                </label>

                {telegramToast && (
                  <div className="mb-3 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-2.5 text-sm font-medium animate-in fade-in slide-in-from-top-2 duration-300">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    {telegramToast}
                  </div>
                )}

                {unlinkMsg && (
                  <div className={`mb-3 flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm ${
                    unlinkMsg.type === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-700'
                      : 'bg-red-50 border border-red-100 text-red-600'
                  }`}>
                    {unlinkMsg.type === 'success'
                      ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                    {unlinkMsg.text}
                  </div>
                )}

                {user?.telegram_id ? (
                  <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                    <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-blue-700">{t('profile.telegramLinked')}</p>
                      <p className="text-xs text-blue-500 font-mono truncate">ID: {user.telegram_id}</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleTelegramUnlink}
                      disabled={isUnlinking}
                      className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-500 bg-white border border-red-100 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-60"
                    >
                      {isUnlinking
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('profile.telegramUnlinking')}</>
                        : <><Unlink className="w-3.5 h-3.5" />{t('profile.telegramUnlink')}</>
                      }
                    </button>
                  </div>
                ) : (
                  <TelegramLinkPanel
                    step={telegramStep}
                    linkData={linkData}
                    onStart={startLink}
                    onRetry={startLink}
                  />
                )}
              </div>

              <div className="flex flex-col gap-3 pt-2 mt-2">
                {!isNotifEditing ? (
                  <button
                    type="button"
                    onClick={handleEditNotifToggle}
                    className="w-full inline-flex justify-center items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 hover:text-gray-900 rounded-xl font-bold hover:bg-gray-200 transition-colors shadow-sm"
                  >
                    {t('profile.editBtn')}
                  </button>
                ) : (
                  <>
                    <button
                      type="submit"
                      disabled={isNotifLoading}
                      className="w-full inline-flex justify-center items-center gap-2 px-6 py-3 bg-goszakup-600 text-white hover:bg-goszakup-700 rounded-xl font-bold transition-colors shadow-sm disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      {isNotifLoading ? '...' : t('profile.saveBtn')}
                    </button>
                    {(initialNotifData.whatsapp || initialNotifData.telegram) && (
                      <button
                        type="button"
                        onClick={handleCancelNotifEdit}
                        disabled={isNotifLoading}
                        className="w-full inline-flex justify-center items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 hover:text-gray-900 rounded-xl font-bold hover:bg-gray-200 transition-colors shadow-sm disabled:opacity-50"
                      >
                        {t('profile.cancelBtn')}
                      </button>
                    )}
                  </>
                )}
              </div>
            </form>
          </div>

          {/* Company Profile */}
          <div id="company" className="scroll-mt-6 workspace-card">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Building2 className="w-6 h-6 text-goszakup-600 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('profile.companyTitle')}</h2>
              <p className="text-sm text-gray-500 mt-0.5">{t('profile.companyDesc')}</p>
            </div>
          </div>
          {!isCompanyEditing && user?.company_profile && (
            <button
              type="button"
              onClick={() => { setIsCompanyEditing(true); setCompanySaveStatus({ type: '', message: '' }); setPdfUploadStatus({ type: '', message: '' }); }}
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl font-bold text-sm transition-colors"
            >
              {t('profile.editBtn')}
            </button>
          )}
        </div>

        {companySaveStatus.message && (
          <div className={`mb-5 p-3 rounded-xl text-sm flex items-center gap-2 ${companySaveStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
            {companySaveStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            {companySaveStatus.message}
          </div>
        )}

        <form onSubmit={saveCompany}>
          {pdfUploadStatus.message && (
            <p role="status" className={`mb-4 rounded-xl border px-3 py-2 text-sm ${pdfUploadStatus.type === 'success' ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-100 bg-red-50 text-red-600'}`}>{pdfUploadStatus.message}</p>
          )}
          {[companyData.income_3yrs, companyData.taxes_3yrs, companyData.pfot_3yrs].some(isFinancialInputComplete)
            && Object.entries(companyData).some(([key, value]) => key.startsWith('history_') && !isFinancialInputComplete(value)) && (
            <p role="status" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-900">{t('pdfImport.historyRequired')}</p>
          )}
          {/* 3-year aggregate fields */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-6">
            {([
              { key: 'income_3yrs', label: t('profile.companyIncome') },
              { key: 'taxes_3yrs', label: t('profile.companyTaxes') },
              { key: 'pfot_3yrs', label: t('profile.companyPfot') },
            ] as { key: keyof typeof companyData; label: string }[]).map(f => (
              <div key={f.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
                {isCompanyEditing ? (
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="decimal"
                      aria-label={f.label}
                      value={companyData[f.key]}
                      onChange={e => setCompanyData(prev => ({ ...prev, [f.key]: fmtInput(e.target.value) }))}
                      placeholder="0"
                      className="block w-full px-4 pr-8 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 text-sm transition-all"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₸</span>
                  </div>
                ) : (
                  <div className="px-4 py-3 bg-gray-50 border border-transparent rounded-xl text-sm font-medium text-gray-900">
                    {companyData[f.key] ? `${companyData[f.key]} ₸` : '—'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Historical year fields */}
          {(() => {
            const cy = new Date().getFullYear();
            const nextYear = cy + 1;
            const y1 = nextYear - 4;
            const y2 = nextYear - 3;
            const rows = [
              {
                fields: [
                  { key: 'history_income_year_1' as const, label: `${t('profile.companyHistoryIncomeLabelBase')} ${y1} ${t('profile.companyHistoryYearWord')}` },
                  { key: 'history_income_year_2' as const, label: `${t('profile.companyHistoryIncomeLabelBase')} ${y2} ${t('profile.companyHistoryYearWord')}` },
                ],
              },
              {
                fields: [
                  { key: 'history_taxes_year_1' as const, label: `${t('profile.companyHistoryTaxesLabelBase')} ${y1} ${t('profile.companyHistoryYearWord')}` },
                  { key: 'history_taxes_year_2' as const, label: `${t('profile.companyHistoryTaxesLabelBase')} ${y2} ${t('profile.companyHistoryYearWord')}` },
                ],
              },
              {
                fields: [
                  { key: 'history_pfot_year_1' as const, label: `${t('profile.companyHistoryPfotLabelBase')} ${y1} ${t('profile.companyHistoryYearWord')}` },
                  { key: 'history_pfot_year_2' as const, label: `${t('profile.companyHistoryPfotLabelBase')} ${y2} ${t('profile.companyHistoryYearWord')}` },
                ],
              },
            ];
            return (
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-700 mb-3">{t('profile.companyHistoryTitle')}</p>
                <div className="space-y-4">
                  {rows.map((row, ri) => (
                    <div key={ri} className="grid grid-cols-1 sm:grid-cols-2 sm:items-end gap-4">
                      {row.fields.map(f => (
                        <div key={f.key}>
                          <label className="block text-xs font-medium text-gray-600 mb-1 leading-snug">{f.label} (₸)</label>
                          {isCompanyEditing ? (
                            <div className="relative">
                              <input
                                type="text"
                                inputMode="decimal"
                                aria-label={`${f.label} (₸)`}
                                value={companyData[f.key]}
                                onChange={e => setCompanyData(prev => ({ ...prev, [f.key]: fmtInput(e.target.value) }))}
                                placeholder="0"
                                className="block w-full px-4 pr-8 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-goszakup-500 focus:border-goszakup-500 text-sm transition-all"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₸</span>
                            </div>
                          ) : (
                            <div className="px-4 py-3 bg-gray-50 border border-transparent rounded-xl text-sm font-medium text-gray-900">
                              {companyData[f.key] ? `${companyData[f.key]} ₸` : '—'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {isCompanyEditing && (
            <>
              {/* PDF Upload */}
              <div className="mb-6">
                <CompanyPdfUpload inputLabel={t('profile.companyPdfTitle')} hint={t('profile.companyPdfHint')}
                  busy={isPdfParsing} onFile={handleCompanyPdfUpload} />

              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3">
                {user?.company_profile && (
                  <button
                    type="button"
                    disabled={isPdfParsing || isCompanyLoading}
                    onClick={() => {
                      const cp = user.company_profile!;
                      setCompanyData({
                        income_3yrs: formatFinancialValue(cp.income_3yrs),
                        taxes_3yrs: formatFinancialValue(cp.taxes_3yrs),
                        pfot_3yrs: formatFinancialValue(cp.pfot_3yrs),
                        history_income_year_1: cp.history_income_year_1 != null ? formatFinancialValue(cp.history_income_year_1) : '',
                        history_income_year_2: cp.history_income_year_2 != null ? formatFinancialValue(cp.history_income_year_2) : '',
                        history_taxes_year_1: cp.history_taxes_year_1 != null ? formatFinancialValue(cp.history_taxes_year_1) : '',
                        history_taxes_year_2: cp.history_taxes_year_2 != null ? formatFinancialValue(cp.history_taxes_year_2) : '',
                        history_pfot_year_1: cp.history_pfot_year_1 != null ? formatFinancialValue(cp.history_pfot_year_1) : '',
                        history_pfot_year_2: cp.history_pfot_year_2 != null ? formatFinancialValue(cp.history_pfot_year_2) : '',
                      });
                      setIsCompanyEditing(false);
                      setCompanySaveStatus({ type: '', message: '' });
                      setPdfUploadStatus({ type: '', message: '' });
                    }}
                    className="inline-flex justify-center items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-xl font-bold transition-colors"
                  >
                    {t('profile.cancelBtn')}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isCompanyLoading || isPdfParsing}
                  className="inline-flex justify-center items-center gap-2 px-6 py-3 bg-goszakup-600 text-white hover:bg-goszakup-700 rounded-xl font-bold transition-colors shadow-sm disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {isCompanyLoading ? t('profile.companySaving') : t('profile.companySave')}
                </button>
              </div>
            </>
          )}
        </form>
          </div>

          {/* ── BILLING SECTION ── */}
          <div id="billing" className="scroll-mt-6 workspace-card space-y-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard className="w-6 h-6 text-goszakup-600" />
          {t('billing.tabTitle')}
        </h2>

        {/* ── Subscription block ── */}
        <div id="subscription" className="scroll-mt-6">
          <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">{t('subscription.blockTitle')}</p>

          {subActionMsg && (
            <div className={`mb-3 p-3 rounded-xl text-sm flex items-center gap-2 ${subActionMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
              {subActionMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {subActionMsg.msg}
            </div>
          )}

          {subscription?.active ? (
            <div className={`rounded-2xl border p-5 ${subscription.status === 'active' ? 'border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white' : 'border-amber-100 bg-amber-50/40'}`}>
              {/* Header */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-2xl bg-goszakup-600 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-extrabold text-gray-900 truncate">{subscription.plan_name}</p>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${subscription.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                      {subscription.status === 'active' ? t('subscription.statusActive') : t('subscription.statusCancelled')}
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-extrabold text-gray-900 tabular-nums">{new Intl.NumberFormat('ru-RU').format(subscription.price_monthly ?? 0)} ₸</p>
                  <p className="text-[11px] text-gray-400">/ {t('subscription.perMonth')}</p>
                </div>
              </div>

              {/* Period info */}
              {subscription.status === 'active' ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                    <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {t('subscription.nextBilling')}: <strong className="text-gray-800">{fmtDate(subscription.next_billing_at)}</strong>
                  </div>
                  <div className={`flex items-start gap-2 text-xs rounded-xl px-3 py-2 mb-4 ${subscription.auto_renew ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-50 text-gray-500'}`}>
                    <RotateCcw className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    {subscription.auto_renew ? t('subscription.autoRenewOn') : t('subscription.autoRenewOff')}
                  </div>
                </>
              ) : (
                <div className="flex items-start gap-2 text-sm bg-white rounded-xl border border-amber-100 px-3 py-2.5 mb-4">
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="text-amber-800">
                    {t('subscription.cancelledUntil').replace('{date}', fmtDate(subscription.ends_at))}
                  </span>
                </div>
              )}

              {/* Archived plan note */}
              {subscription.plan_archived && (
                <div className="flex items-start gap-2 text-xs bg-white rounded-xl border border-gray-200 px-3 py-2.5 mb-4 text-gray-500">
                  <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  {t('subscription.archivedNote')}
                </div>
              )}

              {/* Services breakdown */}
              {subscription.services && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-5">
                  {([
                    { key: 'pfu', label: t('billing.pfuItem') },
                    { key: 'vip', label: t('billing.vipItem') },
                    { key: 'tracker', label: t('billing.slotItem') },
                    { key: 'planning', label: t('billing.planningItem') },
                    { key: 'tier', label: t('billing.tierItem') },
                  ] as { key: string; label: string }[]).map(({ key, label }) => {
                    const cfg = subscription.services?.[key];
                    if (!cfg) return null;
                    if (cfg.included_qty > 0) {
                      return (
                        <div key={key} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <span className="text-gray-600 truncate">{label}</span>
                          <span className="ml-auto font-bold text-gray-800">×{cfg.included_qty}</span>
                        </div>
                      );
                    }
                    if (cfg.discount_pct > 0) {
                      return (
                        <div key={key} className="flex items-center gap-2 text-sm">
                          <span className="w-4 h-4 rounded-full border border-gray-300 flex-shrink-0" />
                          <span className="text-gray-400 truncate">{label}</span>
                          <span className="ml-auto font-bold text-goszakup-600">−{cfg.discount_pct}%</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              )}

              <div className="mb-4"><ProcurementPlanSummary value={subscription.procurement} /></div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => navigate('/plans')}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-goszakup-600 text-white text-sm font-bold hover:bg-goszakup-700 transition-colors"
                >
                  <Package className="w-4 h-4" /> {t('subscription.changePlan')}
                </button>
                {subscription.status === 'active' ? (
                  <button
                    type="button"
                    onClick={() => setShowCancelConfirm(true)}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-bold hover:bg-gray-50 transition-colors"
                  >
                    <X className="w-4 h-4" /> {t('subscription.cancelBtn')}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleResumeSubscription}
                    disabled={isResuming}
                    className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                  >
                    {isResuming ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    {t('subscription.resumeBtn')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="relative overflow-hidden rounded-2xl border border-goszakup-100 bg-gradient-to-br from-goszakup-50 to-white p-5 sm:p-6">
              <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-goszakup-100/40 rounded-full blur-2xl pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-goszakup-600 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-extrabold text-gray-900">{t('subscription.noPlanTitle')}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{t('subscription.noPlanDesc')}</p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/plans')}
                  className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-goszakup-600 text-white text-sm font-bold hover:bg-goszakup-700 transition-colors flex-shrink-0"
                >
                  {t('subscription.choosePlan')} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <ServiceCreditsSummary />

        {/* Saved cards (ePay tokens for auto-renewal) */}
        <div>
          <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">{t('subscription.cardsTitle')}</p>
          <p className="text-xs text-gray-400 mb-3">{t('subscription.cardsHint')}</p>
          {savedCards.length === 0 ? (
            <div className="flex items-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-slate-50/60 px-4 py-3.5 text-xs text-gray-400">
              <CreditCard className="w-4 h-4 flex-shrink-0" />
              {t('subscription.cardsEmpty')}
            </div>
          ) : (
            <div className="space-y-2.5">
              {savedCards.map((card) => (
                <div key={card.id} className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-slate-50 px-4 py-3">
                  <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-goszakup-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 tabular-nums truncate">{card.card_mask || '••••'}</p>
                    <p className="text-[11px] text-gray-400 truncate">
                      {[card.card_type, card.issuer].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmDeleteCard(card)}
                    disabled={deletingCardId === card.id}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-gray-500 hover:text-red-600 hover:border-red-200 disabled:opacity-60 transition-colors flex-shrink-0"
                  >
                    {deletingCardId === card.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                    {t('subscription.cardDeleteBtn')}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-gray-100" />

        {/* Buy limits */}
        <div>
          <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">{t('billing.addTitle')}</p>
          {pricesLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('billing.priceLoading')}
            </div>
          ) : prices ? (() => {
            const fmt = (n: number) => new Intl.NumberFormat('ru-RU').format(n);
            const discOf = (priceKey: string) => prices.discounts?.[priceKey] ?? 0;
            const netPrice = (price: number, pct: number) => Math.max(0, Math.round(price * (1 - pct / 100)));
            const items = [
              { key: 'pfu' as const, label: t('billing.pfuItem'), priceKey: 'pfu_calculation', price: prices.pfu_calculation },
              { key: 'vip' as const, label: t('billing.vipItem'), priceKey: 'vip_calculation', price: prices.vip_calculation },
              { key: 'slot' as const, label: t('billing.slotItem'), priceKey: 'tracker_slot', price: prices.tracker_slot },
              ...(prices.planning_calculation != null ? [{ key: 'planning' as const, label: t('billing.planningItem'), priceKey: 'planning_calculation', price: prices.planning_calculation }] : []),
              ...(prices.tier_requirements_calculation != null ? [{ key: 'tier' as const, label: t('billing.tierItem'), priceKey: 'tier_requirements_calculation', price: prices.tier_requirements_calculation }] : []),
              ...(prices.experience != null ? [{ key: 'experience' as const, label: t('billing.experienceItem'), priceKey: 'experience', price: prices.experience }] : []),
            ];
            const totalNet = items.reduce((s, it) => s + quantities[it.key] * netPrice(it.price, discOf(it.priceKey)), 0);
            const totalFull = items.reduce((s, it) => s + quantities[it.key] * it.price, 0);
            const hasDiscount = totalNet !== totalFull;
            const anyDiscount = items.some((it) => discOf(it.priceKey) > 0);
            const selectedCount = items.reduce((sum, item) => sum + quantities[item.key], 0);
            return (
            <div className="space-y-3">
              {anyDiscount && subscription?.active && (
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-50 border border-amber-100 text-xs font-semibold text-amber-700">
                  <Sparkles className="w-4 h-4 flex-shrink-0" />
                  {t('billing.discountBanner').replace('{plan}', subscription.plan_name ?? '')}
                </div>
              )}
              {items.map(item => {
                const pct = discOf(item.priceKey);
                const net = netPrice(item.price, pct);
                return (
                <div key={item.key} className="flex items-center justify-between bg-slate-50 rounded-2xl px-5 py-4">
                  <div>
                    <p className="text-sm font-bold text-gray-900">{item.label}</p>
                    {pct > 0 ? (
                      <p className="text-xs flex items-center gap-1.5 flex-wrap">
                        <span className="text-gray-400 line-through">{fmt(item.price)} ₸</span>
                        <span className="text-goszakup-700 font-extrabold">{fmt(net)} ₸</span>
                        <span className="inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold">−{pct}%</span>
                        <span className="text-gray-400">/ {t('billing.pieces')}</span>
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400">{fmt(item.price)} ₸ / {t('billing.pieces')}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setQuantities(q => ({ ...q, [item.key]: Math.max(0, q[item.key] - 1) }))}
                      className="w-8 h-8 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold flex items-center justify-center transition-colors"
                    >−</button>
                    <input
                      type="number"
                      min={0}
                      max={999}
                      value={quantities[item.key]}
                      onChange={e => {
                        const raw = parseInt(e.target.value, 10);
                        const v = isNaN(raw) ? 0 : Math.min(999, Math.max(0, raw));
                        setQuantities(q => ({ ...q, [item.key]: v }));
                      }}
                      className="w-14 h-8 text-center font-extrabold text-gray-900 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-goszakup-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                    <button
                      type="button"
                      onClick={() => setQuantities(q => ({ ...q, [item.key]: Math.min(999, q[item.key] + 1) }))}
                      className="w-8 h-8 rounded-xl bg-goszakup-100 hover:bg-goszakup-200 text-goszakup-700 font-bold flex items-center justify-center transition-colors"
                    >+</button>
                  </div>
                </div>
                );
              })}

              {/* Total + Buy */}
              {selectedCount > 0 && (
                <div className="border-t border-gray-100 pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-600">{t('billing.totalLabel')}</span>
                    <span className="flex items-baseline gap-2">
                      {hasDiscount && (
                        <span className="text-sm text-gray-400 line-through">{fmt(totalFull)} ₸</span>
                      )}
                      <span className="text-xl font-extrabold text-gray-900">{fmt(totalNet)} ₸</span>
                    </span>
                  </div>
                  {hasDiscount && (
                    <div className="flex items-center justify-end -mt-2">
                      <span className="text-xs font-bold text-emerald-600">
                        {t('billing.youSave').replace('{amount}', fmt(totalFull - totalNet))}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-center">
                    <PaymentMethodToggle value={buyMethod} onChange={setBuyMethod} t={t} />
                  </div>
                  <button
                    type="button"
                    onClick={handleBuyLimits}
                    disabled={isBuying}
                    className="w-full py-3.5 rounded-2xl text-sm font-extrabold text-white bg-goszakup-600 hover:bg-goszakup-700 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isBuying ? <><Loader2 className="w-4 h-4 animate-spin" />{t('billing.buying')}</> : t('billing.buyBtn')}
                  </button>
                  {billingStatus && (
                    <div className={`p-3 rounded-xl text-sm flex items-center gap-2 ${billingStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                      {billingStatus.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                      {billingStatus.msg}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })() : null}
        </div>
          </div>

        {/* ── SECURITY SECTION ── */}
        <div id="security" className="scroll-mt-6 workspace-card space-y-8">
          <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
            <ShieldCheck className="w-6 h-6 text-goszakup-600" />
            <h2 className="text-lg font-bold text-gray-800">{t('profile.security.title')}</h2>
          </div>

          {/* Security toast */}
          {securityMsg && (
            <div className={`p-3 rounded-xl text-sm flex items-center gap-2 ${securityMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-100'}`}>
              {securityMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {securityMsg.msg}
              <button type="button" onClick={() => setSecurityMsg(null)} className="ml-auto"><X className="w-4 h-4" /></button>
            </div>
          )}

          {/* Active Sessions */}
          <div>
            <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">{t('profile.security.sessionsTitle')}</p>
            {sessionsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse h-20 rounded-2xl bg-gray-100" />
                ))}
              </div>
            ) : sessionsError ? (
              <p className="text-sm text-red-500">{sessionsError}</p>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-gray-400">{t('profile.security.sessionsEmpty')}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {sessions.map(sess => (
                  <div key={sess.session_id} className={`flex items-start gap-3 p-4 rounded-2xl border bg-gray-50 ${
                    currentSessionId && sess.session_id === currentSessionId
                      ? 'border-goszakup-300 bg-goszakup-50'
                      : 'border-gray-100'
                  }`}>
                    <div className="mt-0.5">{getDeviceIcon(sess.user_agent)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-800 truncate">{getDeviceLabel(sess.user_agent)}</p>
                        {currentSessionId && sess.session_id === currentSessionId && (
                          <span className="flex-shrink-0 text-[10px] font-bold text-goszakup-600 bg-goszakup-100 px-1.5 py-0.5 rounded-full">
                            {t('profile.security.currentSession')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">{sess.ip} · {fmtDate(sess.created_at)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setConfirmTerminate(sess)}
                      disabled={terminatingId === sess.session_id}
                      className="flex-shrink-0 text-xs font-semibold text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                    >
                      {terminatingId === sess.session_id ? t('profile.security.sessionTerminating') : t('profile.security.sessionTerminate')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Logout All */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <p className="text-sm text-gray-600 font-medium">{t('profile.security.logoutAll')}</p>
            <button
              type="button"
              onClick={() => setLogoutAllConfirm(true)}
              disabled={logoutAllLoading}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {logoutAllLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {logoutAllLoading ? t('profile.security.logoutAllLoading') : t('profile.security.logoutAll')}
            </button>
          </div>

          {/* Login History */}
          {user?.login_history && user.login_history.length > 0 && (
            <div>
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">{t('profile.security.historyTitle')}</p>
              <div className="rounded-2xl border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase">{t('profile.security.historyIp')}</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase">{t('profile.security.historyDate')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.login_history.slice().reverse().map((entry, i) => (
                      <tr key={i} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-gray-700">{entry.ip}</td>
                        <td className="px-4 py-2.5 text-gray-500">{fmtDateTime(entry.logged_in_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* ── PAYMENT HISTORY SECTION ── */}
        <div id="payments" className="scroll-mt-6 workspace-card space-y-6">
          <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
            <Receipt className="w-6 h-6 text-goszakup-600" />
            <div>
              <h2 className="text-lg font-bold text-gray-800">{t('profile.payments.title')}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{t('profile.payments.subtitle')}</p>
            </div>
          </div>

          {ordersLoading ? (
            <div className="flex items-center justify-center py-12 gap-3 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">{t('profile.payments.loading')}</span>
            </div>
          ) : ordersError ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <p className="text-sm text-red-500 font-medium">{ordersError}</p>
              <button
                type="button"
                onClick={() => fetchOrders(ordersPage)}
                className="px-4 py-2 rounded-xl bg-goszakup-600 text-white text-sm font-bold hover:bg-goszakup-700 transition-colors"
              >
                {t('profile.payments.retry')}
              </button>
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
              <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-1">
                <Receipt className="w-6 h-6 text-gray-300" />
              </div>
              <p className="text-sm font-bold text-gray-500">{t('profile.payments.empty')}</p>
              <p className="text-xs text-gray-400 max-w-xs">{t('profile.payments.emptyDesc')}</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block rounded-2xl border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase whitespace-nowrap">{t('profile.payments.colDate')}</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase">{t('profile.payments.colServices')}</th>
                      <th className="text-right px-4 py-2.5 text-xs font-bold text-gray-400 uppercase whitespace-nowrap">{t('profile.payments.colAmount')}</th>
                      <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase whitespace-nowrap">{t('profile.payments.colStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors align-top">
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtDateTime(o.created_at)}</td>
                        <td className="px-4 py-3 text-gray-700">{renderOrderItems(o.items)}</td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900 whitespace-nowrap">{fmtMoney(o.amount)} ₸</td>
                        <td className="px-4 py-3">{renderOrderStatus(o.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {orders.map((o) => (
                  <div key={o.id} className="rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <span className="text-xs text-gray-400">{fmtDateTime(o.created_at)}</span>
                      {renderOrderStatus(o.status)}
                    </div>
                    <div className="text-sm text-gray-700 mb-2">{renderOrderItems(o.items)}</div>
                    <div className="text-right font-bold text-gray-900">{fmtMoney(o.amount)} ₸</div>
                  </div>
                ))}
              </div>

              <Pagination
                currentPage={ordersPage}
                totalPages={Math.max(1, Math.ceil(ordersTotal / ORDERS_PAGE_SIZE))}
                onPageChange={setOrdersPage}
              />
            </>
          )}
        </div>

        {/* Confirm cancel subscription modal */}
        {showCancelConfirm && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-xl p-6 max-w-sm w-full">
              <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-amber-500" />
              </div>
              <h3 className="font-extrabold text-center text-gray-900 mb-2">{t('subscription.cancelConfirmTitle')}</h3>
              <p className="text-sm text-gray-500 text-center mb-6 leading-relaxed">
                {t('subscription.cancelConfirmBody').replace('{date}', fmtDate(subscription?.current_period_end))}
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCancelConfirm(false)} disabled={isCancelling} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-60">
                  {t('subscription.keepBtn')}
                </button>
                <button type="button" onClick={handleCancelSubscription} disabled={isCancelling} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-60 flex items-center justify-center gap-2">
                  {isCancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('subscription.confirmCancelBtn')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Confirm delete saved card modal */}
        {confirmDeleteCard && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-xl p-6 max-w-sm w-full">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="font-extrabold text-center text-gray-900 mb-2">{confirmDeleteCard.card_mask || '••••'}</h3>
              <p className="text-sm text-gray-500 text-center mb-6 leading-relaxed">
                {t('subscription.cardDeleteConfirm')}
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setConfirmDeleteCard(null)} disabled={!!deletingCardId} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-60">
                  {t('subscription.keepBtn')}
                </button>
                <button type="button" onClick={handleDeleteCard} disabled={!!deletingCardId} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-bold hover:bg-red-600 disabled:opacity-60 flex items-center justify-center gap-2">
                  {deletingCardId ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {t('subscription.cardDeleteBtn')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Confirm terminate session modal — portal ensures it renders above fixed Navigation (z-50) */}
        {confirmTerminate && createPortal(          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-xl p-6 max-w-sm w-full space-y-4">
              {currentSessionId && confirmTerminate.session_id === currentSessionId ? (
                <>
                  <p className="font-bold text-gray-800">{t('profile.security.sessionTerminateSelfTitle')}</p>
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{t('profile.security.sessionTerminateSelfDesc')}</span>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-bold text-gray-800">{t('profile.security.sessionTerminateConfirmTitle')}</p>
                  <p className="text-sm text-gray-500">{t('profile.security.sessionTerminateConfirmDesc')}</p>
                </>
              )}
              <div className="flex gap-3">
                <button type="button" onClick={() => setConfirmTerminate(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                  {t('profile.cancelBtn')}
                </button>
                <button type="button" onClick={() => handleTerminateSession(confirmTerminate)} disabled={!!terminatingId} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50">
                  {terminatingId ? t('profile.security.sessionTerminating') : t('profile.security.sessionTerminate')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Confirm logout all modal — portal ensures it renders above fixed Navigation (z-50) */}
        {logoutAllConfirm && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-3xl shadow-xl p-6 max-w-sm w-full space-y-4">
              <p className="font-bold text-gray-800">{t('profile.security.logoutAllConfirmTitle')}</p>
              <p className="text-sm text-gray-500">{t('profile.security.logoutAllConfirmDesc')}</p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setLogoutAllConfirm(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                  {t('profile.cancelBtn')}
                </button>
                <button type="button" onClick={handleLogoutAll} disabled={logoutAllLoading} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors disabled:opacity-50">
                  {logoutAllLoading ? t('profile.security.logoutAllLoading') : t('profile.security.logoutAll')}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {qrState && (
          <EpayQrModal qr={qrState.qr} polling={qrPolling} onClose={closeQr} t={t} />
        )}
        </div>

        {/* Sticky sidebar nav — desktop only */}
        <aside className="hidden lg:block">
          <nav className="sticky top-8 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <p className="px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider border-b border-gray-100">
              {t('profile.sidebarNav')}
            </p>
            <ul className="py-2">
              {([
                { id: 'profile',       label: 'Основные данные',                    Icon: User },
                { id: 'password',      label: t('profile.passwordTitle'),            Icon: Lock },
                { id: 'notifications', label: t('profile.notifTitle'),               Icon: MessageCircle },
                { id: 'company',       label: t('profile.companyTitle'),             Icon: Building2 },
                { id: 'billing',       label: t('billing.tabTitle'),                 Icon: CreditCard },
                { id: 'security',      label: t('profile.security.title'),           Icon: ShieldCheck },
                { id: 'payments',      label: t('profile.payments.title'),           Icon: Receipt },
              ] as { id: string; label: string; Icon: React.ElementType }[]).map(({ id, label, Icon }) => (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => scrollTo(id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                      activeSection === id
                        ? 'text-goszakup-600 font-semibold bg-goszakup-50'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 font-medium'
                    }`}
                  >
                    <Icon className={`w-4 h-4 flex-shrink-0 ${
                      activeSection === id ? 'text-goszakup-600' : 'text-gray-400'
                    }`} />
                    <span>{label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
      </div>
    </div>
  );
}
